import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Button,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  Center,
  Divider,
  IconButton,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { MdPerson, MdClose } from 'react-icons/md';
import axios from 'axios';
import { CIL } from './constants';

/**
 * Modale lista d'attesa per una variante (settimana) sold out.
 * Mostra i soci in lista in ordine e permette di aggiungere un proprio socio.
 */
const WaitlistModal = ({
  isOpen,
  onClose,
  event,
  variant,
  userData,
  fetchEvent,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [socioToRemove, setSocioToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const {
    isOpen: isRemoveOpen,
    onOpen: onRemoveOpen,
    onClose: onRemoveClose,
  } = useDisclosure();
  const cancelRemoveRef = React.useRef(null);

  const singleTesseramento = userData?.tesseramenti?.length === 1;
  // Usa la variante aggiornata dall'evento (dopo fetchEvent) se disponibile
  const currentVariant =
    event?.Varianti?.find((v) => v.documentId === variant?.documentId) ||
    variant;
  const socios = currentVariant?.waitingList?.socios ?? [];
  const alreadyInList = (docId) =>
    socios.some(
      (s) => s.documentId === docId || (s.socio && s.socio.documentId === docId)
    );

  const socioDocId = (s) =>
    s.documentId || s.socio?.documentId || s.id || s.socio?.id;
  const isMySocio = (s) => {
    const id = socioDocId(s);
    if (!id || !userData?.tesseramenti?.length) return false;
    return userData.tesseramenti.some(
      (t) => String(t.documentId || t.id) === String(id)
    );
  };

  const openRemoveConfirm = (s) => {
    setSocioToRemove(s);
    onRemoveOpen();
  };
  const closeRemoveConfirm = () => {
    setSocioToRemove(null);
    onRemoveClose();
  };

  const handleRemoveFromWaitlist = async () => {
    if (!socioToRemove || !currentVariant?.waitingList?.documentId) return;
    const participantId = socioDocId(socioToRemove);
    if (!participantId) return;
    setRemoving(true);
    setError('');
    try {
      await axios.post('/api/waiting-list/remove', {
        waitingListId: currentVariant.waitingList.documentId,
        participantId,
      });
      closeRemoveConfirm();
      fetchEvent?.();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Errore durante l'uscita dalla lista d'attesa. Riprova o contatta il supporto."
      );
    } finally {
      setRemoving(false);
    }
  };

  useEffect(() => {
    if (singleTesseramento && userData?.tesseramenti?.[0]) {
      const docId = userData.tesseramenti[0].documentId;
      if (
        !socios.some(
          (s) =>
            s.documentId === docId || (s.socio && s.socio.documentId === docId)
        )
      ) {
        setSelectedParticipant(docId);
      }
    }
  }, [singleTesseramento, userData, variant?.documentId, socios]);

  const handleSignUp = async () => {
    if (!selectedParticipant || !event?.documentId || !variant?.documentId)
      return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/waiting-list', {
        eventId: event.documentId,
        variantId: variant.documentId,
        participantId: selectedParticipant,
      });
      if (res.data.waitingList) {
        setSuccess(true);
        setSelectedParticipant(null);
        fetchEvent?.();
        setTimeout(() => setSuccess(false), 4000);
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Errore durante l'iscrizione alla lista d'attesa. Contatta il supporto."
      );
    } finally {
      setLoading(false);
    }
  };

  // Per privacy mostriamo solo la posizione; se è un socio dell'utente loggato mostriamo il nome (da userData)
  const getMySocioName = (s) => {
    const id = socioDocId(s);
    if (!id || !userData?.tesseramenti) return null;
    const t = userData.tesseramenti.find(
      (tess) => String(tess.documentId || tess.id) === String(id)
    );
    return t ? [t.nomeSocio, t.cognomeSocio].filter(Boolean).join(' ') : null;
  };
  const displayListLabel = (s, index) => {
    const myName = getMySocioName(s);
    if (myName) return `${index + 1}. ${myName}`;
    return `Posizione ${index + 1}`;
  };

  if (!variant) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            Lista d&apos;attesa – {variant.Nome || 'Settimana'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {success && (
              <Alert status="success" borderRadius="md" mb={4}>
                <AlertIcon />
                Iscrizione alla lista d&apos;attesa avvenuta con successo. Puoi
                aggiungere un altro socio qui sotto.
              </Alert>
            )}
            <Text fontWeight="bold" mb={2} color={CIL.plum}>
              Soci in lista (in ordine)
            </Text>
            {socios.length === 0 ? (
              <Text color="gray.600" mb={4}>
                Nessun socio in lista al momento.
              </Text>
            ) : (
              <List spacing={2} mb={4}>
                {socios.map((s, index) => (
                  <ListItem
                    key={s.documentId || s.socio?.documentId || index}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    py={1}
                  >
                    <ListIcon as={MdPerson} color={CIL.sky} flexShrink={0} />
                    <Text as="span" flex={1} minW={0}>
                      {displayListLabel(s, index)}
                    </Text>
                    {userData && isMySocio(s) && (
                      <IconButton
                        type="button"
                        aria-label="Esci dalla lista d'attesa"
                        icon={<MdClose />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        flexShrink={0}
                        ml="auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          openRemoveConfirm(s);
                        }}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            )}

            <Divider my={4} />

            <Text fontWeight="bold" mb={3} color={CIL.plum}>
              Aggiungi un socio alla lista
            </Text>
            {!userData ? (
              <Box>
                <Text color="gray.600" mb={3}>
                  Accedi per iscriverti alla lista d&apos;attesa con uno dei
                  tuoi soci.
                </Text>
                <Button
                  colorScheme="teal"
                  onClick={() =>
                    navigate(`/login?redirect=/centro-estivo-2026`)
                  }
                >
                  Accedi
                </Button>
              </Box>
            ) : (
              <>
                {error && (
                  <Alert status="error" borderRadius="md" mb={4}>
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                {!singleTesseramento && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mb={4}>
                    {userData.tesseramenti.map((t) => {
                      const disabled = alreadyInList(t.documentId);
                      const selected = selectedParticipant === t.documentId;
                      return (
                        <Box
                          key={t.documentId}
                          p={3}
                          borderRadius="lg"
                          borderWidth="2px"
                          borderColor={selected ? CIL.sky : 'gray.200'}
                          bg={selected ? 'blue.50' : 'white'}
                          cursor={disabled ? 'not-allowed' : 'pointer'}
                          opacity={disabled ? 0.6 : 1}
                          onClick={() =>
                            !disabled && setSelectedParticipant(t.documentId)
                          }
                        >
                          <Text fontSize="sm" fontWeight="bold">
                            {t.nomeSocio} {t.cognomeSocio}
                          </Text>
                          {disabled && (
                            <Text fontSize="xs" color="gray.500">
                              Già in lista
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </SimpleGrid>
                )}
                <Center>
                  <Button
                    colorScheme="green"
                    onClick={handleSignUp}
                    isLoading={loading}
                    disabled={!selectedParticipant}
                  >
                    Iscriviti alla lista d&apos;attesa
                  </Button>
                </Center>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              Chiudi
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isRemoveOpen}
        leastDestructiveRef={cancelRemoveRef}
        onClose={closeRemoveConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              Esci dalla lista d&apos;attesa
            </AlertDialogHeader>
            <AlertDialogBody>
              {socioToRemove && (
                <>
                  Sei sicuro di volerti togliere dalla lista d&apos;attesa
                  {getMySocioName(socioToRemove)
                    ? ` per ${getMySocioName(socioToRemove)}`
                    : ''}
                  ? La rimozione è immediata.
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRemoveRef} onClick={closeRemoveConfirm}>
                Annulla
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRemoveFromWaitlist}
                isLoading={removing}
                ml={3}
              >
                Esci dalla lista
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default WaitlistModal;
